import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ArticlesGrid from '../components/ArticlesGrid';
import { useSiteData } from '../data/SiteDataContext';
import { getSeriesPath } from '../lib/format';

// A writer-created series: its name, description and every part in reading
// order. Title-detected series have no page (they open on their first part).
function SeriesPage() {
    const { slug } = useParams();
    const { status, seriesStatus, posts, series } = useSiteData();

    if (status === 'loading') {
        return <p className="small-meta site-loading">Loading …</p>;
    }

    if (status === 'error') {
        return <p className="small-meta site-loading">Failed to load documents. Refresh to retry.</p>;
    }

    const wanted = String(slug || '').toLowerCase();
    const entry = series.find((item) => item.slug && item.slug === wanted) || null;
    if (!entry) {
        // Writer series failed to load: the slug may well exist, so keep
        // the URL (a refresh retries) instead of sending it to the index.
        if (seriesStatus === 'error') {
            return <p className="small-meta site-loading">Couldn’t load this series. Refresh to retry.</p>;
        }
        return <Navigate to="/series" replace />;
    }
    // One URL per series: /series/World-War-Lies -> /series/world-war-lies.
    if (slug !== entry.slug) {
        return <Navigate to={getSeriesPath(entry)} replace />;
    }

    const postById = new Map(posts.map((post) => [String(post._id), post]));
    const partNumberById = new Map(entry.parts.map((part) => [String(part.postId), part.part]));
    const partPosts = entry.parts
        .map((part) => postById.get(String(part.postId)))
        .filter(Boolean);
    const firstPart = entry.parts[0];
    const partCount = entry.parts.length;

    return (
        <>
            <header className="archive-header series-header">
                <div className="eyebrow" style={{ marginBottom: '.4rem' }}>
                    <Link to="/series">Series</Link>
                </div>
                <h1>{entry.name}</h1>
                {entry.description && (
                    <p className="series-header-description">{entry.description}</p>
                )}
                <div className="series-header-meta">
                    <span className="small-meta">{`${partCount} part${partCount === 1 ? '' : 's'}`}</span>
                    {firstPart && (
                        <>
                            <span className="sep">·</span>
                            <Link className="series-header-start" to={`/posts/${firstPart.postId}`}>
                                Start with Part 1 →
                            </Link>
                        </>
                    )}
                </div>
            </header>
            <ArticlesGrid
                posts={partPosts}
                getCardLabel={(post) => `Part ${partNumberById.get(String(post._id))}`}
            />
        </>
    );
}

export default SeriesPage;
